/** TWILIO_CONVERSATIONS_SERVICE control: live-read the operator's Twilio Conversations Service
 *  and show its identity + reachability. When the live probe confirms the service, the backend
 *  advances the item to completed; this control refetches so the row status reflects it.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { items } from '@/api/preflight';
import type { PreflightItem, TwilioConversationsDetail } from '@/api/preflight';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { twilioConversationsActions } from '../tenantLinks';

export function TwilioConversationsControl({
  item,
  topId,
  canManage,
  onChanged,
}: {
  item: PreflightItem;
  topId: number;
  canManage: boolean;
  onChanged: () => void;
}) {
  const seed = item.detail as unknown as TwilioConversationsDetail;
  const query = useQuery({
    queryKey: ['preflight', 'twilioConversationsStatus', topId, item.id],
    queryFn: () => items.fetchTwilioConversationsStatus(topId, item.id),
    enabled: canManage,
  });
  const status = query.data;
  const detail = status?.detail ?? seed;

  // The backend advances the item to completed once the live probe confirms the service; refetch
  // the preflight list so the row status reflects it (guarded on item.status so it runs once).
  useEffect(() => {
    if (status?.ok && status.detail?.service_present && item.status !== 'completed') {
      onChanged();
    }
  }, [status, item.status, onChanged]);

  if (status?.reason === 'not_configured') {
    return (
      <Box sx={{ mb: 1.5 }}>
        <Alert severity="info">
          Twilio isn&apos;t configured on the server, so the Conversations Service can&apos;t be
          queried.
        </Alert>
      </Box>
    );
  }

  const present = detail?.service_present === true;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1.5}>
        {detail?.error && (
          <Alert severity="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {detail.error}
          </Alert>
        )}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {present ? (
            <CheckCircleIcon fontSize="small" color="success" />
          ) : (
            <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
          )}
          <Typography variant="body2">
            {present
              ? 'Conversations Service confirmed on Twilio'
              : detail?.service_sid
                ? 'Service SID set — not yet confirmed on Twilio'
                : 'No Conversations Service SID set'}
          </Typography>
          {query.isFetching && <CircularProgress size={14} />}
        </Stack>

        {detail?.service_sid && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              columnGap: 1.5,
              rowGap: 0.25,
              alignItems: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Service SID
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {/* Same URL shape the backend's preflight resources use. */}
              <ValueActions actions={twilioConversationsActions(detail.service_sid)}>
                {detail.service_sid}
              </ValueActions>
            </Typography>
            {detail.friendly_name && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Friendly name
                </Typography>
                <Typography variant="body2">{detail.friendly_name}</Typography>
              </>
            )}
            {detail.reachability_enabled != null && (
              <>
                <Typography variant="caption" color="text.secondary">
                  Reachability
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={detail.reachability_enabled ? 'success' : 'default'}
                  label={detail.reachability_enabled ? 'enabled' : 'disabled'}
                  sx={{ justifySelf: 'start' }}
                />
              </>
            )}
          </Box>
        )}

        {canManage ? (
          <Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              Refresh from Twilio
            </Button>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            You need manage permission to query the live Twilio service.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
